export type EvidenceType = '截图' | '录音' | '视频' | '聊天记录' | '文件解析';

export interface EvidenceTypeTab {
  key: string;
  label: string;
  icon: string;
  count: number;
}

export interface EvidenceCase {
  id: string;
  title: string;
  summary: string;
  type: EvidenceType;
  source: string;
  time: string;
  files: number;
  heat: number;
  verified: boolean;
  image: string;
}

export interface HeroStat {
  label: string;
  value: string;
  unit: string;
  icon: string;
}

export interface TimelineNode {
  id: string;
  date: string;
  clock: string;
  title: string;
  detail: string;
  type: EvidenceType;
}

export interface ParsedFile {
  id: string;
  name: string;
  kind: string;
  size: string;
  hash: string;
  status: string;
  progress: number;
}

export interface CommentItem {
  id: string;
  user: string;
  avatarText: string;
  time: string;
  content: string;
  likes: number;
  replies: number;
  tag: string;
}

export interface RankItem {
  id: string;
  rank: number;
  title: string;
  type: EvidenceType;
  heat: number;
  trend: 'up' | 'down' | 'new';
  delta: string;
}

export const evidenceTypeTabs: EvidenceTypeTab[] = [
  { key: '全部', label: '全部证据', icon: 'ri-archive-2-line', count: 1286 },
  { key: '截图', label: '截图链', icon: 'ri-image-2-line', count: 486 },
  { key: '录音', label: '录音原件', icon: 'ri-mic-2-line', count: 197 },
  { key: '视频', label: '视频实拍', icon: 'ri-file-video-line', count: 154 },
  { key: '聊天记录', label: '聊天记录', icon: 'ri-chat-history-line', count: 302 },
  { key: '文件解析', label: '文件解析', icon: 'ri-file-search-line', count: 147 },
];

export const heroStats: HeroStat[] = [
  { label: '已存档证据', value: '12,480', unit: '份', icon: 'ri-folder-shield-2-line' },
  { label: '今日更新', value: '26', unit: '条', icon: 'ri-refresh-line' },
  { label: '时间线节点', value: '318', unit: '个', icon: 'ri-time-line' },
  { label: '参与对线', value: '47,900', unit: '人', icon: 'ri-group-line' },
];

export const evidenceCases: EvidenceCase[] = [
  {
    id: 'case-01',
    title: '「星河商贸」直播间退货截图链',
    summary: '38 张直播后台截图按时间排序，退货按钮、话术脚本与库存数字前后矛盾处已用红框逐张标注，原图未压缩直接留档。',
    type: '截图',
    source: '前员工投稿',
    time: '2026-09-08 21:40',
    files: 38,
    heat: 9821,
    verified: true,
    image:
      'https://readdy.ai/api/search-image?query=Flat%20lay%20of%20printed%20chat%20screenshots%20with%20red%20annotation%20marks%20on%20a%20jade%20green%20desk%2C%20blurred%20personal%20details%2C%20warm%20studio%20lighting%2C%20clean%20minimal%20cream%20background%2C%20editorial%20still%20life%20photography%20with%20soft%20shadows&width=1200&height=800&seq=gt-case-01&orientation=landscape',
  },
  {
    id: 'case-02',
    title: '主播「甜十七」私下录音原件流入',
    summary: '三段总长 26 分钟的对话录音，涉及选品报价与分成口径，已附降噪版与原始波形对照，可逐句定位时间码。',
    type: '录音',
    source: '匿名线人',
    time: '2026-09-08 18:05',
    files: 3,
    heat: 8740,
    verified: true,
    image:
      'https://readdy.ai/api/search-image?query=Studio%20microphone%20and%20audio%20waveform%20printout%20on%20a%20sand%20colored%20table%2C%20warm%20amber%20desk%20lighting%2C%20minimal%20cream%20background%2C%20editorial%20product%20photography%20with%20soft%20shadow%20and%20clean%20composition&width=800&height=600&seq=gt-case-02&orientation=landscape',
  },
  {
    id: 'case-03',
    title: '「云栖数码」发货现场视频实拍',
    summary: '门店打包区 12 条监控片段拼接，展示型号替换与二次封装全过程，时间码已对齐，关键帧逐张截取留档。',
    type: '视频',
    source: '内部监控',
    time: '2026-09-07 15:22',
    files: 12,
    heat: 7630,
    verified: true,
    image:
      'https://readdy.ai/api/search-image?query=Screenshot%20frame%20of%20a%20shipping%20warehouse%20video%20shown%20on%20a%20monitor%2C%20stacked%20packages%20and%20timestamp%20overlay%2C%20muted%20cinematic%20tones%2C%20clean%20cream%20desk%20background%2C%20editorial%20documentary%20photography&width=800&height=600&seq=gt-case-03&orientation=landscape',
  },
  {
    id: 'case-04',
    title: '某联名品牌客服聊天记录全档',
    summary: '从咨询到售后共 240 条完整对话，客服承诺与最终处理结果逐条对照，矛盾点已编号并生成对照表。',
    type: '聊天记录',
    source: '消费者合辑',
    time: '2026-09-07 11:10',
    files: 240,
    heat: 6980,
    verified: false,
    image:
      'https://readdy.ai/api/search-image?query=Neatly%20arranged%20printed%20chat%20log%20pages%20with%20highlight%20markers%2C%20jade%20green%20and%20cream%20palette%2C%20soft%20daylight%2C%20minimal%20editorial%20still%20life%20photography%20on%20a%20wooden%20desk&width=800&height=600&seq=gt-case-04&orientation=landscape',
  },
  {
    id: 'case-05',
    title: '「夜航工作室」合约文件解析报告',
    summary: '合同 PDF 拆解出 9 处修改痕迹，签署时间戳与流水日期存在 11 天缺口，解析报告与原始哈希一并公开。',
    type: '文件解析',
    source: '文档投稿',
    time: '2026-09-06 20:33',
    files: 9,
    heat: 6120,
    verified: true,
    image:
      'https://readdy.ai/api/search-image?query=Contract%20document%20pages%20with%20data%20analysis%20annotations%2C%20magnifier%20and%20calculator%20on%20a%20cream%20desk%2C%20warm%20neutral%20tones%2C%20clean%20minimal%20composition%2C%20editorial%20photography%20with%20soft%20light&width=800&height=600&seq=gt-case-05&orientation=landscape',
  },
  {
    id: 'case-06',
    title: '「橙子不熬夜」账号矩阵截图合集',
    summary: '跨平台 17 个关联账号主页截图，注册信息与简介文案高度雷同，归属链路已连线整理成一张关系图。',
    type: '截图',
    source: '网友整理',
    time: '2026-09-06 14:47',
    files: 17,
    heat: 5540,
    verified: false,
    image:
      'https://readdy.ai/api/search-image?query=Two%20smartphone%20screens%20showing%20social%20media%20profile%20grids%20on%20a%20sand%20colored%20surface%2C%20soft%20long%20shadow%2C%20warm%20minimal%20editorial%20photography%20with%20clean%20cream%20background&width=800&height=600&seq=gt-case-06&orientation=landscape',
  },
  {
    id: 'case-07',
    title: '联名款预售录音双轨对账存档',
    summary: '预售直播录音与后台对账录音双轨并存，价格口径差异出现在第 14 分钟与第 41 分钟，已截取原始语句。',
    type: '录音',
    source: '录屏投稿',
    time: '2026-09-05 22:15',
    files: 2,
    heat: 4980,
    verified: true,
    image:
      'https://readdy.ai/api/search-image?query=Audio%20editing%20timeline%20on%20a%20laptop%20screen%20with%20headphones%20beside%20it%2C%20warm%20amber%20desk%20lighting%2C%20clean%20minimal%20cream%20background%2C%20editorial%20technology%20photography%20with%20soft%20shadows&width=800&height=600&seq=gt-case-07&orientation=landscape',
  },
  {
    id: 'case-08',
    title: '「北岸咖啡」后厨视频存档',
    summary: '后厨操作间 6 段录像，涉及原料更换与标签撕改画面，关键帧已逐张截取，原始文件保留不动。',
    type: '视频',
    source: '门店内部',
    time: '2026-09-05 09:30',
    files: 6,
    heat: 4310,
    verified: true,
    image:
      'https://readdy.ai/api/search-image?query=Kitchen%20surveillance%20footage%20frame%20visible%20on%20a%20tablet%20screen%20placed%20on%20a%20wooden%20counter%2C%20muted%20colors%2C%20clean%20warm%20cream%20composition%2C%20editorial%20documentary%20photography&width=800&height=600&seq=gt-case-08&orientation=landscape',
  },
];

export const timelineNodes: TimelineNode[] = [
  {
    id: 'tl-06',
    date: '09 / 08',
    clock: '21:40',
    title: '退货截图链完成归档',
    detail: '38 张后台截图完成编号入库，矛盾点红框标注，原始分辨率文件同步留存。',
    type: '截图',
  },
  {
    id: 'tl-05',
    date: '09 / 08',
    clock: '18:05',
    title: '录音原件入库并完成降噪',
    detail: '三段录音保留原始波形，另外输出一份降噪版，方便逐句核对报价口径。',
    type: '录音',
  },
  {
    id: 'tl-04',
    date: '09 / 07',
    clock: '15:22',
    title: '发货视频时间码对齐',
    detail: '12 条监控片段按时间码拼接，型号替换节点用黄色标记单独导出。',
    type: '视频',
  },
  {
    id: 'tl-03',
    date: '09 / 07',
    clock: '11:10',
    title: '聊天记录全档补齐',
    detail: '240 条对话补齐缺失时段，客服承诺与处理结果生成逐条对照表。',
    type: '聊天记录',
  },
  {
    id: 'tl-02',
    date: '09 / 06',
    clock: '20:33',
    title: '合约文件解析报告出炉',
    detail: '9 处修改痕迹定位完成，签署时间戳与流水日期缺口锁定为 11 天。',
    type: '文件解析',
  },
  {
    id: 'tl-01',
    date: '09 / 05',
    clock: '22:15',
    title: '预售录音双轨比对完成',
    detail: '直播录音与对账录音并排比对，两处价格口径差异点完成时间定位。',
    type: '录音',
  },
];

export const parsedFiles: ParsedFile[] = [
  {
    id: 'file-01',
    name: 'yehang_contract_signed.pdf',
    kind: 'PDF',
    size: '4.8 MB',
    hash: 'a3f1...9c2e',
    status: '已解析',
    progress: 100,
  },
  {
    id: 'file-02',
    name: 'live_return_log_0908.xlsx',
    kind: 'XLSX',
    size: '1.2 MB',
    hash: '7b90...41da',
    status: '已解析',
    progress: 100,
  },
  {
    id: 'file-03',
    name: 'call_0908_26min_raw.mp3',
    kind: 'MP3',
    size: '24.6 MB',
    hash: 'e5c2...88f0',
    status: '波形比对中',
    progress: 68,
  },
  {
    id: 'file-04',
    name: 'warehouse_cam_batch_12.mp4',
    kind: 'MP4',
    size: '312 MB',
    hash: '19af...77b3',
    status: '关键帧提取',
    progress: 45,
  },
  {
    id: 'file-05',
    name: 'chat_log_lianming_240.txt',
    kind: 'TXT',
    size: '860 KB',
    hash: 'c24d...0e91',
    status: '待复核',
    progress: 12,
  },
];

export const comments: CommentItem[] = [
  {
    id: 'cm-01',
    user: '瓜田夜巡',
    avatarText: '夜',
    time: '2 小时前',
    content: '第 14 分钟那段录音，和 9 月 6 日文件解析里的签署日期能对上，建议把两条时间线合并着看，缺口就更明显了。',
    likes: 328,
    replies: 12,
    tag: '时间线锤',
  },
  {
    id: 'cm-02',
    user: '带壳的瓜',
    avatarText: '壳',
    time: '3 小时前',
    content: '截图里库存数字前后差了 200 多件，这个点比话术脚本更实，建议单独做一个对照小图放出来。',
    likes: 214,
    replies: 6,
    tag: '实锤证据',
  },
  {
    id: 'cm-03',
    user: '锤子收藏家',
    avatarText: '锤',
    time: '5 小时前',
    content: '文件解析报告最好把原始哈希一起放出，这样大家自己就能校验有没有被动过，不然总有人说是 P 的。',
    likes: 186,
    replies: 9,
    tag: '文件解析',
  },
  {
    id: 'cm-04',
    user: '晚风对线',
    avatarText: '晚',
    time: '昨天 23:10',
    content: '聊天记录第 88 条客服口径和最后的售后结果完全相反，我已经截图存本地了，免得回头被删。',
    likes: 152,
    replies: 4,
    tag: '聊天记录',
  },
];

export const rankItems: RankItem[] = [
  {
    id: 'rk-01',
    rank: 1,
    title: '星河商贸退货截图链',
    type: '截图',
    heat: 9821,
    trend: 'up',
    delta: '+12.4%',
  },
  {
    id: 'rk-02',
    rank: 2,
    title: '主播甜十七私下录音原件',
    type: '录音',
    heat: 8740,
    trend: 'up',
    delta: '+8.1%',
  },
  {
    id: 'rk-03',
    rank: 3,
    title: '云栖数码发货现场实拍',
    type: '视频',
    heat: 7630,
    trend: 'new',
    delta: '新入榜',
  },
  {
    id: 'rk-04',
    rank: 4,
    title: '联名客服聊天记录全档',
    type: '聊天记录',
    heat: 6980,
    trend: 'down',
    delta: '-3.2%',
  },
  {
    id: 'rk-05',
    rank: 5,
    title: '夜航工作室合约解析报告',
    type: '文件解析',
    heat: 6120,
    trend: 'up',
    delta: '+5.6%',
  },
  {
    id: 'rk-06',
    rank: 6,
    title: '预售录音双轨对账存档',
    type: '录音',
    heat: 4980,
    trend: 'down',
    delta: '-1.8%',
  },
];